import React, { memo, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Checkbox, FormControlLabel } from '@material-ui/core';

import colors from '../styles/Colors';
import ReservationConfirm from '../containers/Order/Renter/Create/ReservationConfirm';
import { displayFlex, doMediaQuery } from '../styles/Mixins';
import { ORDER_FOR_VIEW_DETAILS } from 'Queries/Renter/OrderForViewDetails';
import { HeadingOne } from '../components/Headings';
import IndeterminateLoading from 'Components/Loading/IndeterminateLoading';
import BackArrow from '../assets/img/icons/back-arrow.png';
import { PageNotFoundSection } from 'Components/PageNotFound/PageNotFoundSection';
import { isErrorCode } from 'Utils/graphqlErrorHelper';
import { graphqlErrorCodes } from 'Types/enums/graphqlEnums';
import { addProtocolToUrl } from 'Utils/urls';
import { UserContext } from 'Store/UserContext';
import { GET_RENTER_CONFIRMATION_AGREEMENT } from 'Queries/Renter/getRenterConfirmationAgreement';
import { CREATE_RENTER_CONFIRMATION_AGREEMENT } from 'Queries/Renter/updateRenterConfirmationAgreement';
import Modal from '../components/Modal';
import Button from '../components/Button';
import { mapSummaryFromResponse } from 'Helpers/mapSummaryFromResponse';
import { formatTime } from 'Helpers/formatTime';
const formatDate = date => (date ? date.format('ddd, MMM D, YYYY') : '');

const RenterConfirmReservation = props => {
  const { className } = props;
  const params = useParams();
  const { user } = useContext(UserContext);
  const isViewDetailsPage = props.match.url.indexOf('details/') > 1;
  const isViewConfirmationPage = props.match.url.indexOf('confirmation/') > 1;
  const [needsRenterAgreement, setNeedsRenterAgreement] = useState(false);
  const [showRenterAgreementPopup, setShowRenterAgreementPopup] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getOrder, { data, error, loading }] = useLazyQuery(ORDER_FOR_VIEW_DETAILS, {
    variables: {
      id: params.orderId
    },
    fetchPolicy: 'network-only'
  });

  const [getRenterConfirmationAgreement, { data: renterAgreement, error: renterAgreementError, loading: renterAgreementLoading, called }] = useLazyQuery(
    GET_RENTER_CONFIRMATION_AGREEMENT,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [createRenterConfirmationAgreement] = useMutation(CREATE_RENTER_CONFIRMATION_AGREEMENT);

  // get data info
  useEffect(() => {
    if ((isViewDetailsPage || params.orderId) && !data) getOrder();
  }, [data]);

  // get renter agreement
  useEffect(() => {
    if (data?.order?.event?.venue?.id) {
      const venue = user.venues.find(v => v.id === data?.order?.event?.venue?.id);
      if (venue?.venueConfig?.promptRenterAgreement) {
        setNeedsRenterAgreement(true);
        getRenterConfirmationAgreement({
          variables: {
            input: {
              userId: user.id,
              orderId: params.orderId
            }
          }
        });
      }
    }
  }, [data?.order]);

  // renter agreement trigger
  useEffect(() => {
    if (
      needsRenterAgreement &&
      !renterAgreementLoading &&
      called &&
      (!renterAgreement?.getRenterConfirmationAgreement?.agreement?.id || !renterAgreement?.getRenterConfirmationAgreement?.agreement?.confirmed)
    ) {
      setShowRenterAgreementPopup(true);
      const oData = mapSummaryFromResponse({ order: data.order });
      setOrderData(oData);
    }
  }, [renterAgreement, renterAgreementError, needsRenterAgreement, renterAgreementLoading]);

  const handleAcceptTerms = async () => {
    if (!termsAccepted) return;

    setIsSubmitting(true);

    try {
      const response = await createRenterConfirmationAgreement({
        variables: {
          input: {
            userId: user.id,
            orderId: params.orderId,
            confirmed: true
          }
        }
      });

      if (response?.data?.createRenterConfirmationAgreement?.agreement?.confirmed) {
        setShowRenterAgreementPopup(false);
        setNeedsRenterAgreement(false);
        setOrderData(null);
        setTermsAccepted(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = event => {
    setTermsAccepted(event.target.checked);
  };

  const orderWasNotFound = () => isErrorCode(error, graphqlErrorCodes.BAD_USER_INPUT);

  if (orderWasNotFound()) {
    return <PageNotFoundSection className={className} />;
  }

  if (loading || error) {
    return <div style={{ marginTop: '100px' }}>{loading ? <IndeterminateLoading /> : 'Error retrieving orders'}</div>;
  }

  let titleHeader = 'Reservation Summary';
  if (data && !moment(data.order.createdAt).isSame(data.order.updatedAt)) titleHeader = 'Reservation Updated';

  let message = '';

  if (data?.order.canceled) {
    message = 'Your reservation has been canceled. You will receive a copy of this confirmation via email.';
  } else if (data?.order?.group) {
    message = 'Your reservation is confirmed. You will receive a copy of this confirmation via email.';
  } else {
    message = 'Your reservation is confirmed and has been paid in full. You will receive a copy of this confirmation via email.';
  }

  const links = data?.order?.event?.eventLinks
    ?.filter(l => !!l.url)
    ?.map(l => {
      return {
        ...l,
        url: addProtocolToUrl(l.url)
      };
    });

  return (
    <>
      <section className={className}>
        <FlexWrapper>
          <div className={`${className}__Page-Title`}>
            {isViewDetailsPage && (
              <Link to="/reservations" data-testid="back-to-reservations-button" id={`back-button`}>
                <img src={BackArrow} alt={'BACK'} /> BACK TO RESERVATIONS
              </Link>
            )}
            <HeadingOne className={`${className}__Header`} label={isViewDetailsPage ? 'Order Details' : 'Confirmation'} />
            <p className={`${className}__Lead`}>{message}</p>
          </div>
        </FlexWrapper>
        <FlexWrapper>
          {links && links.length > 0 && (
            <EventLinksStyled>
              {links.map(l => (
                <div className="link-wrapper" key={`link-${l.id}`}>
                  <span>{l.description.trim()} </span>
                  <span className={`link-span ${l.description ? 'with-margin' : ''}`}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer">
                      {l.label ? l.label : l.url}
                    </a>
                  </span>
                </div>
              ))}
            </EventLinksStyled>
          )}
        </FlexWrapper>
        <ReservationConfirm
          isViewDetailsPage={isViewDetailsPage}
          isViewConfirmationPage={isViewConfirmationPage}
          data={data}
          titleHeader={titleHeader}
          checkoutResponse={props.location.state}
        />
      </section>
      {orderData && orderData.event && (
        <RenterAgreementModal heading={'Acknowledgment and Agreement'} open={showRenterAgreementPopup}>
          <p>Please review your booking confirmation and acknowledge that you understand the check-in and check-out requirements for your reservation.</p>
          <div className="stalls">
            {orderData.stalls.quantity > 0 && (
              <>
                <div className="summary-row">
                  <h2>Stall Summary</h2>
                  <div className={'summary-item'}>
                    <span>
                      <strong>Check In: </strong>
                    </span>
                    <span>
                      {formatDate(orderData.stalls.start)} • After {formatTime(orderData.event.checkInTime)}
                    </span>
                  </div>
                  <div className={'summary-item next-row'}>
                    <span>
                      <strong>Check Out: </strong>
                    </span>
                    <span>
                      {formatDate(orderData.stalls.end)} • By {formatTime(orderData.event.checkOutTime)}
                    </span>
                  </div>
                  <div className={'summary-item'}>
                    <span>
                      <strong>Quantity: </strong>
                    </span>
                    <span>{orderData.stalls.quantity}</span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="rvs">
            {orderData.rv_spot.quantity > 0 && (
              <>
                <div className="summary-row">
                  <h2>RV Spot Summary</h2>
                  <div className={'summary-item'}>
                    <span>
                      <strong>Check In: </strong>
                    </span>
                    <span>
                      {formatDate(orderData.rv_spot.start)} • After {formatTime(orderData.event.checkInTime)}
                    </span>
                  </div>
                  <div className={'summary-item next-row'}>
                    <span>
                      <strong>Check Out: </strong>
                    </span>
                    <span>
                      {formatDate(orderData.rv_spot.end)} • By {formatTime(orderData.event.checkOutTime)}
                    </span>
                  </div>
                  <div className={'summary-item'}>
                    <span>
                      <strong>Quantity: </strong>
                    </span>
                    <span>{orderData.rv_spot.quantity}</span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="terms-checkbox">
            <FormControlLabel
              control={<Checkbox checked={termsAccepted} onChange={handleCheckboxChange} color="primary" data-testid="terms-and-conditions-checkbox" />}
              label="I have reviewed and understand the check-in/check-out times and booking policies"
            />
          </div>
          <div className="accept-button">
            <Button primary disabled={!termsAccepted || isSubmitting} isLoading={isSubmitting} onClick={handleAcceptTerms}>
              Acknowledge & Finish
            </Button>
          </div>
        </RenterAgreementModal>
      )}
    </>
  );
};

const RenterConfirmReservationStyled = styled(RenterConfirmReservation)`
  margin: 85px auto 0;
  max-width: 1284px;
  &__Page-Title {
    text-align: left;
    ${doMediaQuery(
      'DESKTOP_WIDTH',
      `
      margin: 0 55px;
    `
    )}
  }
  &__Header {
    &&& {
      margin-top: 10px;
      text-align: left;
      @media screen and (max-width: 1270px) {
        margin-left: 4%;
      }
    }
  }
  #back-button {
      text-align: left;
      font-size: 15px;
      color: ${colors.border.tertiary};
      letter-spacing: 1.05px;
      line-height: 17px;
      margin-bottom: 20px !important;
      cursor: pointer;

      img {
        height: 11px;
        margin-right: 8px;
      }
    }
  }

  &__Lead {
    &&& {
      text-align: left;
      @media screen and (max-width: 1270px) {
        margin-left: 4%;
      }
      font-family: 'IBMPlexSans-Regular';
      font-size: 16px;
    }
  }
`;

const FlexWrapper = styled.div`
  ${displayFlex}
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const EventLinksStyled = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;

  .link-span {
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 190px;

    &.with-margin {
      margin-left: 5px;
    }
  }

  .link-wrapper {
    display: flex;
  }

  ${doMediaQuery(
    'DESKTOP_WIDTH',
    `
      margin: 0 55px;
    `
  )}

  @media screen and (max-width: 1270px) {
    margin-left: 4%;
  }

  margin-bottom: 20px !important;
`;

const RenterAgreementModal = styled(Modal)`
  .terms-checkbox {
    margin-top: 20px;
    margin-bottom: 10px;
  }

  .accept-button {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    button {
      height: 50px !important;
      width: 250px !important;
    }
  }
`;

export default memo(RenterConfirmReservationStyled);
